import { toast } from "react-toastify";

/**
 * Parses and decodes a JWT token payload
 * @param {string} token 
 * @returns {object|null}
 */
export const parseJwt = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Checks if a JWT token is structurally valid and unexpired
 * @param {string} token 
 * @returns {boolean}
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload) return false;
  // If exp exists in JWT, check that current time has not passed it
  if (payload.exp && payload.exp * 1000 <= Date.now()) {
    return false;
  }
  return true;
};

/**
 * Retrieves the stored JWT token from localStorage if valid
 * @returns {string|null}
 */
export const getAuthToken = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.token || parsed?.Token;
    if (token && isTokenValid(token)) {
      return token;
    }
    // If token exists but expired, clean it up
    if (token && !isTokenValid(token)) {
      localStorage.removeItem("user");
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Retrieves the current authenticated user profile from localStorage
 * @returns {object|null}
 */
export const getAuthUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.token || parsed?.Token;
    if (token && isTokenValid(token)) {
      return parsed;
    }
    localStorage.removeItem("user");
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Checks if current user is logged in with a valid unexpired JWT
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Attaches Authorization Bearer header if a valid token is present
 * @param {object} customHeaders 
 * @returns {object}
 */
export const getAuthHeaders = (customHeaders = {}) => {
  const token = getAuthToken();
  const headers = { ...customHeaders };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Clears stored authentication session
 */
export const logoutUser = () => {
  try {
    localStorage.removeItem("user");
  } catch (e) {
    console.warn("Could not remove user from localStorage:", e);
  }
};

/**
 * Handles HTTP 401 Unauthorized API responses
 */
export const handleAuthError = (customMessage) => {
  logoutUser();
  toast.error(customMessage || "Session expired or unauthorized. Please log in again.");
  setTimeout(() => {
    window.location.href = "/equity-login";
  }, 800);
};

/**
 * Safely parses permissions JSON string or returns existing object
 * @param {string|object} value 
 * @returns {object}
 */
export const parsePermissions = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value) || {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" ? value : {};
};

/**
 * Checks if the current authenticated user has permission to perform an action on a module.
 * SuperAdmin and users with {"all": true} bypass all checks.
 * @param {string} module - E.g. "users", "invoices", "bank_details", "services", "settings", "enquiries", "taxes", "complaints", "payments", "customers"
 * @param {string} action - E.g. "read", "create", "update", "delete", "send_email", "download", etc.
 * @returns {boolean}
 */
export const hasPermission = (module, action = "read") => {
  const auth = getAuthUser();
  const user = auth?.user || auth?.User || auth || {};
  const role = String(user.role || user.Role || "").toLowerCase();

  // 1. SuperAdmin / Root bypass
  if (role === "superadmin" || role === "root") {
    return true;
  }

  // Fallback: check role from JWT claims if missing on user object
  const token = getAuthToken();
  if (token) {
    const claims = parseJwt(token);
    const tokenRole = String(
      claims?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
      claims?.role ||
      ""
    ).toLowerCase();
    if (tokenRole === "superadmin" || tokenRole === "root") {
      return true;
    }
  }

  // 2. Parse permissions from user object or JWT claims
  let permissions = parsePermissions(user.permissions ?? user.Permissions);
  if (!permissions || Object.keys(permissions).length === 0) {
    if (token) {
      const claims = parseJwt(token);
      permissions = parsePermissions(claims?.permissions);
    }
  }

  // 3. Global wildcard ("all": true or "*": true)
  if (permissions?.all === true || permissions?.["*"] === true) {
    return true;
  }

  // 4. Module-level wildcard ("invoices": true)
  if (permissions?.[module] === true) {
    return true;
  }

  // 5. Action check within module array
  const modulePerms = permissions?.[module];
  if (Array.isArray(modulePerms)) {
    if (modulePerms.includes("*")) return true;
    if (modulePerms.includes(action)) return true;

    // Action alias matching (e.g. read <=> view)
    const aliases = {
      read: ["read", "view", "list", "get"],
      view: ["read", "view", "list", "get"],
      update: ["update", "edit", "modify"],
      edit: ["update", "edit", "modify"],
      delete: ["delete", "remove"],
    };
    const equivalentActions = aliases[action] || [];
    return modulePerms.some((act) => equivalentActions.includes(act));
  }

  return false;
};

